import uniq from "lodash/uniq";
import uniqBy from "lodash/uniqBy";

import { supabase } from "@/lib/supabase";
import { StarNumber } from "@/types/sos-types";
import { Tables } from "@/types/supabase-types";
import { validateCurrencyCode } from "@/utils/dinero-util";
import { getSosStarEmoji, StarEmoji } from "@/utils/sos/utilities";
import { CADCurrencyConverter } from "./currency";
import { ItemReceipt } from "./item-receipts";
import { calculateBaseQuantity, convertQuantityToUOM, itemUOMSelect, ItemUOMSelectResult } from "./item-uoms";
import { itemSelect, ItemSelectResult } from "./items";
import { createLineSummaryForJob, ProcessedPurchasingLine, PurchaseCostsSummary } from "./purchasing-line-item";
import { PurchaseTaxCode, vendorPricingInformationSelect, VendorWithPricingInformation } from "./vendor";

export interface PurchaseOrder extends Omit<PurchaseOrderSelectResult, "lines"> {
  starEmoji: StarEmoji;
  receivedJobLines: number;
  linkedReceipts: PurchaseOrderLineItemSelectResult["linkedItemReceiptLineItems"][0]["itemReceipt"][];
  allLines: PurchaseOrderSelectResult["lines"];
  jobLines: PurchaseOrderLineItem[];
  jobLinesSummary: PurchaseCostsSummary;
}

export interface PurchaseOrderLineItem extends ProcessedPurchasingLine<PurchaseOrderLineItemSelectResult> {
  /** Quantity for the line item converted to the base UOM */
  baseQuantity: number;
  /** Quantity received for this line item, converted to match the selected UOM units on the PO line */
  uomReceived: number;
  linkedLineReceipts: PurchaseOrderLineItemSelectResult["linkedItemReceiptLineItems"][0]["itemReceipt"][];
}

interface PurchaseOrderSelectResult extends Tables<"PurchaseOrder"> {
  vendor: VendorWithPricingInformation;
  currency: Pick<Tables<"Currency">, "code">;
  lines: PurchaseOrderLineItemSelectResult[];
}

export interface PurchaseOrderLineItemSelectResult extends Pick<
  Tables<"PurchaseOrderLineItem">,
  "id" | "lineNumber" | "description" | "unitprice" | "amount"
> {
  item: ItemSelectResult;
  itemUOM: ItemUOMSelectResult | null;
  /** Quantity for the line item in the selected ItemUOM */
  uomQuantity: number;
  taxCode: PurchaseTaxCode | null;
  job: Pick<Tables<"Job">, "id" | "name"> | null;
  linkedItemReceiptLineItems: LinkedItemReceiptLineItem[];
}

interface LinkedItemReceiptLineItem {
  itemUOM: ItemUOMSelectResult | null;
  /** Quantity for the line item in the selected ItemUOM */
  uomQuantity: number;
  itemReceipt: Pick<Tables<"ItemReceipt">, "id" | "number">;
}

const purchaseOrderSelect = `
*,
vendor:Vendor!inner(${vendorPricingInformationSelect}),
currency:Currency!inner(code),
lines:PurchaseOrderLineItem!inner(
  id,
  lineNumber,
  description,
  unitprice,
  amount,
  uomQuantity:quantity,
  itemUOM:ItemUOM!PurchaseOrderLineItem_itemId_uomId_fkey(${itemUOMSelect}),
  item:Item!inner(${itemSelect}),
  taxCode:TaxCode!taxCodeId(id,name,purchaseTaxRate),
  job:Job(id, name),
  linkedItemReceiptLineItems:ItemReceiptLineItem(
    uomQuantity:quantity,
    itemUOM:ItemUOM!ItemReceiptLineItem_itemId_uomId_fkey(${itemUOMSelect}),
    itemReceipt:ItemReceipt!inner(id, number)
  )
)
`;

export async function getPurchaseOrders(
  jobId: number,
  itemReceipts: ItemReceipt[],
  CADCurrencyConverter: CADCurrencyConverter,
): Promise<PurchaseOrder[]> {
  // Get all PO ids based on non-deleted PO line items assigned to the job
  const jobMatchingPOIdsResponse = await supabase
    .from("PurchaseOrderLineItem")
    .select("purchaseOrderId")
    .eq("deleted", false)
    .eq("jobId", jobId)
    .throwOnError();
  const jobMatchingPOIds = jobMatchingPOIdsResponse.data.map((x) => x.purchaseOrderId);

  // Get all PO ids based on linked item receipt line items assigned to the job
  const jobMatchingReceiptLines = itemReceipts.flatMap((x) => x.jobLines);
  const receiptMatchingPoIds = jobMatchingReceiptLines
    .filter((x) => x.linkedPurchaseOrderLineItem)
    .map((x) => x.linkedPurchaseOrderLineItem!.purchaseOrder.id);

  // Get the POs from Supabase
  const allMatchingPOIds = uniq([...jobMatchingPOIds, ...receiptMatchingPoIds]);
  const queryResult = (
    await supabase
      .from("PurchaseOrder")
      .select<typeof purchaseOrderSelect, PurchaseOrderSelectResult>(purchaseOrderSelect)
      .in("id", allMatchingPOIds)
      .eq("deleted", false)
      .eq("lines.deleted", false)
      .order("date", { ascending: false })
      .order("lineNumber", { referencedTable: "lines", ascending: true })
      .throwOnError()
  ).data;

  // Do some additional client-side processing on the results before returning
  return queryResult.map((po) => processPo(po, jobId, CADCurrencyConverter));
}

function processPo(
  { lines: allLines, ...po }: PurchaseOrderSelectResult,
  jobId: number,
  CADCurrencyConverter: CADCurrencyConverter,
): PurchaseOrder {
  // Process the line items; Do some common processing first, then continue doing some additional processing specific to POs
  const currencyCode = validateCurrencyCode(po.currency.code);
  const { jobLines: preProcessedJobLines, jobLinesSummary } = createLineSummaryForJob(
    allLines,
    currencyCode,
    po.exchangeRate,
    jobId,
    CADCurrencyConverter,
  );
  const jobLines = preProcessedJobLines.map((line) => processPoLineItem(line));

  // Get all linked receipts for the PO (for the current job only)
  const linkedReceipts = uniqBy(
    jobLines.flatMap((line) => line.linkedLineReceipts),
    (x) => x.id,
  );

  // Count the number of job line items that have been fully received
  const receivedJobLines = jobLines.filter((x) => x.uomReceived === x.uomQuantity).length;

  return {
    ...po,
    starEmoji: getSosStarEmoji(po.starred as StarNumber),
    linkedReceipts,
    allLines,
    jobLines,
    jobLinesSummary,
    receivedJobLines,
  };
}

function processPoLineItem(poLine: ProcessedPurchasingLine<PurchaseOrderLineItemSelectResult>): PurchaseOrderLineItem {
  // Calculate the total received quantity for the line item (convert to the UOM units selected in PO)
  // NOTE: This value is also available directly from the SOS API, but we cannot use it because our sync process to supabase
  // is not able to keep it up to date so it is not included in Supabase
  const uomReceived = poLine.linkedItemReceiptLineItems.reduce(
    (sum, receiptLine) => sum + convertQuantityToUOM(receiptLine.uomQuantity, receiptLine.itemUOM, poLine.itemUOM),
    0,
  );

  // Calculate the base quantity based on the UOM
  const baseQuantity = calculateBaseQuantity(poLine.uomQuantity, poLine.itemUOM);

  // Get all linked receipts for the current line item
  const linkedLineReceipts = uniqBy(
    poLine.linkedItemReceiptLineItems.filter((x) => x.uomQuantity).map((x) => x.itemReceipt),
    (x) => x.id,
  );

  return { ...poLine, baseQuantity, uomReceived, linkedLineReceipts };
}
